import React, { useEffect } from 'react';
import { Wrapper } from '../../../components/Wrapper';
import {
  Box,
  Text,
  Input,
  InputGroup,
  Button,
  Flex,
  Icon,
  Select,
  Grid,
  useBreakpointValue,
  Img,
  Divider,
  GridItem,
  List,
  ListItem,
  Image,
  Stack,
} from '@chakra-ui/react';
import { FaSlidersH } from 'react-icons/fa';
import { BsStack } from 'react-icons/bs';
import { If, Then, Else } from 'react-if';
import Skeleton from 'react-loading-skeleton';
import { useIsAuthenticated } from 'react-auth-kit';
import { Placement } from 'react-joyride';
import { checkAndRedirectToProfile } from '../../../../utils/authUtils';

import PengetahuanController, {
  usePengetahuanContext,
} from '../../../controller/pengetahuan/index';
import PengetahuanBackground from '../../../assets/webp/bg_pengetahuan.webp';
import PengetahuanBackgroundMobile from '../../../assets/webp/bg_pengetahuan_mobile.webp';
import LogoPintar from '../../../assets/png/logo_pintar_black.png';
import Pagination from '../../../components/Pagination';
import CardItem from '../../../components/global/Card/CardItem';
import JoyrideComponent from '../../../components/global/Joyride';
import CardItemSync from '../../../components/global/Card/CardItemSync';

/**
 * Component for displaying the hero section of the knowledge center page.
 * @returns {JSX.Element} The HeroPelatihan component.
 * @author Bagas Prasetyadi
 */

const HeroPengetahuan: React.FC = () => {
  const { setState, fetchData } = usePengetahuanContext();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const onSubmitSearch = (e) => {
    e.preventDefault();
    fetchData({ jenis_pengetahuan: 'asynchronized' });
    fetchData({ jenis_pengetahuan: 'synchronized' });
  };

  return (
    <Box
      backgroundImage={{
        base: `url(${PengetahuanBackgroundMobile})`,
        md: `url(${PengetahuanBackground})`,
      }}
      h={{ base: '230px', sm: '350px', md: '52vh' }}
      w="auto"
      backgroundSize="cover"
      display="flex"
      justifyContent={{ base: 'center', md: 'start' }}
      alignItems="center"
    >
      <Flex
        justifyContent="center"
        alignItems={{ base: 'center', md: 'start' }}
        flexDirection="column"
        ml={{ base: 0, md: '8rem' }}
        mt={{ base: '-50px', md: '-2rem' }}
        w={{ base: '90%', md: 'auto' }}
      >
        {isMobile ? null : <Img src={LogoPintar} w="150px" alt="Logo Pintar" />}
        <Text
          mt={{ base: 0, md: 8 }}
          fontSize={{ base: '2xl', md: '5xl' }}
          fontWeight="bold"
          color="black"
          textAlign={{ base: 'center', md: 'left' }}
          letterSpacing="tight"
        >
          Mari Belajar Melalui Pusat
          <br />
          Pengetahuan
        </Text>
        <Box mt={{ base: 2, md: 6 }} w="full">
          <form onSubmit={(e) => onSubmitSearch(e)} id="search-pengetahuan">
            <InputGroup h={{ base: 8, md: 12 }}>
              <Input
                type="text"
                placeholder="Cari Materi"
                onChange={(e) => {
                  setState((prev) => ({
                    ...prev,
                    keyword: e.target.value,
                  }));
                }}
                pr="8rem"
                bg="white"
                border="1px"
                borderColor="green.700"
                rounded="xl"
                h="full"
                w="90%"
                mr={3}
                fontSize="sm"
                _hover={{ border: 'green.700' }}
                _focus={{
                  borderColor: 'green.700',
                  ring: '1px',
                  ringColor: 'green.700',
                }}
              />

              <Button
                rounded="xl"
                w={{ base: 16, md: 20 }}
                h="full"
                type="submit"
                bg="green.700"
                color="white"
                _hover={{ bg: 'green.800' }}
                _active={{ bg: 'green.800' }}
              >
                Cari
              </Button>
            </InputGroup>
          </form>
        </Box>
      </Flex>
    </Box>
  );
};

const GuidePengetahuan = () => {
  const steps = [
    {
      content: (
        <p>
          Search Pengetahuan: Temukan pengetahuan baru yang ingin kamu ketahui
          disini
        </p>
      ),
      placement: 'bottom' as Placement,
      target: '#search-pengetahuan',
      disableBeacon: true,
    },
    {
      content: <p>Lihat detail dan informasi pengetahuan, disini</p>,
      placement: 'bottom' as Placement,
      target: '.pengetahuan-item:nth-child(1)',
      disableBeacon: true,
    },
  ];

  return (
    <Box mt={{ base: 4, md: 0 }}>
      <JoyrideComponent steps={steps} />
    </Box>
  );
};

const GridPengetahuan = ({ listData, isLoading }) => {
  return (
    <>
      <div className="mt-4">
        <If condition={isLoading}>
          <Then>
            <Skeleton
              count={3}
              style={{ height: '280px', width: '380px', marginRight: '20px' }}
              inline={true}
            />
          </Then>
          <Else>
            <If condition={listData.length > 0}>
              <Then>
                <Grid
                  gap={4}
                  templateColumns={{
                    base: 'repeat(1fr)',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                  }}
                >
                  {listData.map((item, key) => {
                    return item.jenis_pengetahuan === 'synchronized' ? (
                      <CardItemSync
                        item={item}
                        spacing={false}
                        key={key}
                        accessFor="pengetahuan"
                      />
                    ) : (
                      <CardItem
                        item={item}
                        spacing={false}
                        key={key}
                        accessFor="pengetahuan"
                      />
                    );
                  })}
                </Grid>
              </Then>
              <Else>
                <Box textAlign="center" mt={20}>
                  <Text fontSize="xl" color="gray.400">
                    Belum Ada Pengetahuan
                  </Text>
                </Box>
              </Else>
            </If>
          </Else>
        </If>
      </div>
    </>
  );
};

/**
 * Component for displaying the list of knowledge items.
 * @returns {JSX.Element} The ListPengetahuan component.
 */

const ListPengetahuan: React.FC = () => {
  const { fetchData, setState, getListKategoriPengetahuan } =
    usePengetahuanContext();
  const {
    isLoading,
    listData,
    category,
    pagination,
    page,
    perPage,
    listKategoriPengetahuan,
    kategori_pengetahuan_id,
    sort_by,
    listDataSync,
    paginationSync,
    pageSync,
    perPageSync,
    isLoadingSync,
  } = usePengetahuanContext().state;

  const isMobile = useBreakpointValue({ base: true, md: false });

  const totalPage = pagination ? Math.ceil(pagination.total_rows / perPage) : 0;

  const totalPageSync = paginationSync
    ? Math.ceil(paginationSync.total_rows / perPageSync)
    : 0;

  const getTotalDataPengetahuan = () => {
    let totalData = 0;
    if (pagination && paginationSync) {
      totalData = pagination.total_rows + paginationSync.total_rows;
    }

    const result = totalData > 100 ? '100+' : totalData;
    return result;
  };

  const handlePageChange = (e, type) => {
    setState((prev) => ({
      ...prev,
      [type]: e.selected + 1,
    }));
  };

  useEffect(() => {
    fetchData({ jenis_pengetahuan: 'asynchronized' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, kategori_pengetahuan_id, sort_by, page, perPage]);

  useEffect(() => {
    fetchData({ jenis_pengetahuan: 'synchronized' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, kategori_pengetahuan_id, sort_by, pageSync, perPageSync]);

  useEffect(() => {
    getListKategoriPengetahuan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box mx={{ base: 8, md: 12 }} my={8}>
      <Flex
        alignItems={{ base: 'start', md: 'center' }}
        justifyContent="space-between"
        w="full"
        flexDirection={{ base: 'column', md: 'row' }}
      >
        <Text fontSize="lg" fontWeight="semibold" color="green.700">
          {' '}
          Ditemukan {getTotalDataPengetahuan()} Pengetahuan
        </Text>

        <Box
          position="relative"
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          mb={{ base: 4, md: 0 }}
        >
          <Box position="relative" marginRight={4}>
            <Icon
              as={FaSlidersH}
              position="absolute"
              mt={3}
              ml={3}
              boxSize={4}
            />
            <Select
              w={{ base: '180px', md: '250px' }}
              h={10}
              border="1px"
              borderColor="gray.500"
              rounded="md"
              _focus={{
                ring: '1',
                borderColor: 'green.700',
                ringColor: 'green.700',
              }}
              _hover={{
                borderColor: 'gray.500',
              }}
              fontSize={{ base: 'sm', md: 'md' }}
              _placeholder={{ color: 'gray.300' }}
              style={{ paddingLeft: '40px' }}
              defaultValue=""
              onChange={(e) => {
                setState((prev) => ({
                  ...prev,
                  sort_by: e.target.value,
                }));
              }}
            >
              <option value="terbaru">Terbaru</option>
              <option value="penayangan terbanyak">Penayangan Terbanyak</option>
              <option value="rating tertinggi">Rating Tertinggi</option>
            </Select>
          </Box>

          {!isMobile && <GuidePengetahuan />}
        </Box>
      </Flex>
      <Grid templateColumns={'repeat(6, 1fr)'} gap={4} mt={4}>
        {/* Sidebar */}
        <GridItem colSpan={{ base: 6, md: 1 }}>
          <Box bg="white" rounded="lg" py={2}>
            <Text fontSize="md" fontWeight="semibold" textAlign="center">
              Kategori Pengetahuan
            </Text>
            <Divider my={2} />
            <List spacing={2}>
              <ListItem
                onClick={() => {
                  setState((prev) => ({
                    ...prev,
                    kategori_pengetahuan_id: '',
                  }));
                }}
                cursor="pointer"
                bg={
                  kategori_pengetahuan_id === '' ? 'green.100' : 'transparent'
                }
                _hover={{ bg: 'green.100', color: 'green.800' }}
              >
                <Stack direction="row" alignItems="center" px={2}>
                  <Icon as={BsStack} w="1.5rem" h="1.2rem" />
                  <Text
                    color={
                      kategori_pengetahuan_id === '' ? 'green.700' : 'gray.500'
                    }
                  >
                    Semua
                  </Text>
                </Stack>
              </ListItem>
              {listKategoriPengetahuan.map((item, i) => (
                <ListItem
                  key={i}
                  onClick={() => {
                    setState((prev) => ({
                      ...prev,
                      kategori_pengetahuan_id: item.value.toString(),
                    }));
                  }}
                  cursor="pointer"
                  bg={
                    kategori_pengetahuan_id === item.value.toString()
                      ? 'green.100'
                      : 'transparent'
                  }
                  _hover={{ bg: 'green.100', color: 'green.800' }}
                >
                  <Stack direction="row" alignItems="center" px={2}>
                    <Image
                      src={item.icon}
                      alt={`Icon ${item.label}`}
                      boxSize="1.5rem"
                    />

                    <Text
                      color={
                        kategori_pengetahuan_id === item.value.toString()
                          ? 'green.800'
                          : 'gray.500'
                      }
                    >
                      {item.label} ({item.total})
                    </Text>
                  </Stack>
                </ListItem>
              ))}
            </List>
          </Box>
        </GridItem>
        <GridItem colSpan={{ base: 6, md: 5 }}>
          <Text fontSize={{ base: 'lg', md: '2xl' }} fontWeight="semibold">
            Klinik Pengetahuan
          </Text>
          {isMobile && <GuidePengetahuan />}

          {/* Pengetahuan Sync */}
          <GridPengetahuan listData={listDataSync} isLoading={isLoadingSync} />

          {paginationSync && totalPageSync > 1 && (
            <Flex justifyContent="end" mt={8}>
              <Pagination
                handleChange={(e) => handlePageChange(e, 'pageSync')}
                totalPage={totalPageSync}
                page={pageSync}
              />
            </Flex>
          )}

          <Divider my={6} />

          <Text fontSize={{ base: 'lg', md: '2xl' }} fontWeight="semibold">
            {/* Ditemukan {paginationSync ? paginationSync?.total_rows : 0} Pengetahuan */}
            Pengetahuan Online Learning
          </Text>
          {/* Pengetahuan Async */}
          <GridPengetahuan listData={listData} isLoading={isLoading} />

          {pagination && totalPage > 1 && (
            <Flex justifyContent="center" mt={8}>
              <Pagination
                handleChange={(e) => handlePageChange(e, 'page')}
                totalPage={totalPage}
                page={page}
              />
            </Flex>
          )}
        </GridItem>
      </Grid>
    </Box>
  );
};

const WrapperMain: React.FC = () => {
  // use is authenticated
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    checkAndRedirectToProfile(isAuthenticated());
  }, [isAuthenticated]);

  return (
    <Wrapper>
      {/* Hero */}
      <HeroPengetahuan />

      {/* List Pengetahuan */}
      <ListPengetahuan />
    </Wrapper>
  );
};

const Main: React.FC = () => {
  return (
    <PengetahuanController.Provider>
      <WrapperMain />
    </PengetahuanController.Provider>
  );
};

export default Main;
